import { createParticipantOverlay } from '../participantOverlay';
import { watchPicture } from '../../streamPicture';

// An auto-arranged grid of every participant's camera - the "Dynamic grid" layer. Shows the call
// owner's own camera plus every guest the host has put on stage (not just anyone who has joined -
// matches the "the host can add you to the broadcast at any time" guest experience). `excludeHost`
// leaves the owner's own tile out, for a guests-only grid. Needs an active call session
// (host.callContext); otherwise it explains why it's empty rather than showing fakes.
//
// The owner tile uses callContext.onOwnerStream, not onOwnStream - on the guest page those are two
// different people's cameras (see camera.js's own comment on the same distinction).
const OWNER_ID = '__owner__';
const TILE_ASPECT = 16 / 9;
const GAP = 4;

export default async function mountCameraGrid(host) {
	// Seeds host.current with the layer's own settings before anything reads them.
	await host.loadSettings();
	const grid = document.createElement('div');
	Object.assign(grid.style, {
		width: '100%', height: '100%', display: 'grid', gap: `${GAP}px`,
		background: 'rgba(0,0,0,.2)',
	});
	const placeholder = document.createElement('div');
	Object.assign(placeholder.style, {
		width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center',
		background: 'rgba(255,255,255,.08)', color: '#fff', fontSize: '.8rem', textAlign: 'center', padding: '.5rem',
	});
	host.container.appendChild(placeholder);
	host.container.appendChild(grid);
	grid.style.display = 'none';

	const tiles = new Map(); // callerId | OWNER_ID -> { el, video, receiver? }
	let lastCallers = [];
	let lastOwnerStream;
	let hasOwnerStream = false;

	function showEmpty(text) {
		placeholder.textContent = text;
		placeholder.style.display = 'flex';
		grid.style.display = 'none';
	}

	// The layer can be resized to any shape, so the columns follow the box: whichever count gives the largest
	// 16:9 picture per cell (a wide box gets a row, a tall one a column, a square one a 2x2). Tiles then fill their
	// cell, cropping a little where the cell isn't quite 16:9.
	function pickColumns(count, width, height) {
		if(!width || !height) return Math.max(1, Math.ceil(Math.sqrt(count)));
		let best = 1;
		let bestArea = 0;
		for(let cols = 1; cols <= count; cols++) {
			const rows = Math.ceil(count / cols);
			const cellW = (width - GAP * (cols - 1)) / cols;
			const cellH = (height - GAP * (rows - 1)) / rows;
			const w = Math.min(cellW, cellH * TILE_ASPECT);
			const area = w * (w / TILE_ASPECT);
			if(area > bestArea + 0.5) { bestArea = area; best = cols; }
		}
		return best;
	}

	// What the scene already shows in layers of their own - left out of the grid unless the layer says otherwise.
	let claims = { callerIds: new Set(), streamIds: new Set(), camera: false };
	let ownScreenId = null; // the studio's own share (the host isn't in its own caller list)
	const hidesClaimed = () => host.current?.hideSpeakers !== false;

	// Fit keeps every picture whole at 16:9 and lets the group sit anywhere in the layer (hAlign / vAlign);
	// Fill (the default) stretches the cells over the whole layer and crops each picture to its cell, with the
	// alignment picking which part of the picture is kept.
	const setting = (key, fallback) => host.current?.[key] || fallback;
	const isFit = () => setting('fit', 'fill') === 'fit';
	const gridDisplay = () => isFit() ? 'flex' : 'grid';
	const FLEX_ALIGN = { left: 'flex-start', top: 'flex-start', center: 'center', right: 'flex-end', bottom: 'flex-end' };

	let tileCount = 0;
	function layoutGrid(count = tileCount) {
		tileCount = count;
		if(!count) return;
		const fit = isFit();
		const hAlign = setting('hAlign', 'center');
		const vAlign = setting('vAlign', 'center');
		if(grid.style.display !== 'none') grid.style.display = gridDisplay();
		const width = grid.clientWidth;
		const height = grid.clientHeight;
		const cols = pickColumns(count, width, height);
		const rows = Math.ceil(count / cols);
		const cellW = (width - GAP * (cols - 1)) / cols;
		const cellH = (height - GAP * (rows - 1)) / rows;
		const tileW = Math.floor(Math.min(cellW, cellH * TILE_ASPECT));
		Object.assign(grid.style, fit ? {
			gridTemplateColumns: '', gridTemplateRows: '',
			flexWrap: 'wrap', justifyContent: FLEX_ALIGN[hAlign], alignContent: FLEX_ALIGN[vAlign], alignItems: 'flex-start',
		} : {
			gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`, gridTemplateRows: `repeat(${rows}, minmax(0, 1fr))`,
			flexWrap: '', justifyContent: '', alignContent: '', alignItems: '',
		});
		for(const tile of tiles.values()) {
			Object.assign(tile.el.style, fit && width && height
				? { flex: '0 0 auto', width: `${tileW}px`, height: `${Math.floor(tileW / TILE_ASPECT)}px` }
				: { flex: '', width: '', height: '' });
			Object.assign(tile.video.style, { objectFit: fit ? 'contain' : 'cover', objectPosition: `${hAlign} ${vAlign}` });
		}
	}

	function makeTile() {
		const el = document.createElement('div');
		Object.assign(el.style, { position: 'relative', overflow: 'hidden', background: '#000', minWidth: '0', minHeight: '0' });
		const video = document.createElement('video');
		video.autoplay = true;
		video.muted = true;
		video.playsInline = true;
		Object.assign(video.style, { width: '100%', height: '100%', objectFit: 'cover', display: 'none' });
		el.appendChild(video);
		grid.appendChild(el);
		// Name, "no camera" placeholder and speaking ring (a participant can be on stage mic-only).
		const overlay = createParticipantOverlay(el);
		overlay.setCameraOff(true);
		return { el, video, overlay };
	}

	function setTileStream(tile, stream) {
		tile.stopWatching?.();
		tile.video.srcObject = stream || null;
		tile.overlay.setStream(stream);
		// The video stays displayed either way (the placeholder covers it): watching its frames needs it playing.
		tile.video.style.display = stream ? 'block' : 'none';
		// A screen share sends few frames while nothing on it moves, so it's not frame-watched.
		tile.stopWatching = watchPicture(stream, has => tile.overlay.setCameraOff(!has), { video: tile.isScreen ? null : tile.video });
	}

	function addTile(entry) {
		const tile = makeTile();
		tile.isScreen = entry.kind === 'screen';
		if(entry.kind === 'owner') {
			tile.video.muted = true; // never echo the local mic back
			setTileStream(tile, lastOwnerStream);
		} else {
			tile.receiver = host.callContext.openReceiver(entry.callerId, entry.kind === 'screen' ? entry.streamId : undefined);
			tile.receiver.onStream(stream => setTileStream(tile, stream));
		}
		tiles.set(entry.id, tile);
	}

	function removeTile(id) {
		const tile = tiles.get(id);
		if(!tile) return;
		tile.receiver?.close();
		tile.stopWatching?.();
		tile.overlay.destroy();
		tile.el.remove();
		tiles.delete(id);
	}

	// Who the grid shows, in display order: the owner and their screen, then each guest on stage (in the order they
	// joined) with theirs. With `applyClaims`, anyone the scene already shows in a layer of their own is left out.
	function listEntries(applyClaims) {
		const hide = applyClaims && hidesClaimed();
		const ownerCaller = lastCallers.find(c => c.isOwner);
		const ownerId = host.callContext.ownCallerId?.() || ownerCaller?.callerId;
		const ownerName = host.callContext.ownName?.() || ownerCaller?.name || '';
		const entries = [];
		const addScreen = (callerId, streamId, name) => {
			if(streamId && !(hide && claims.streamIds.has(streamId))) entries.push({ id: `screen:${streamId}`, kind: 'screen', callerId, streamId, name: `${name} (screen)` });
		};
		if(!host.current?.excludeHost) {
			if(hasOwnerStream && !(hide && (claims.camera || (ownerId && claims.callerIds.has(ownerId))))) {
				entries.push({ id: OWNER_ID, kind: 'owner', name: ownerName, muted: host.callContext.ownMicMuted?.() ?? !!ownerCaller?.micMuted });
			}
			addScreen(ownerId, ownScreenId || ownerCaller?.screenId, ownerName);
		}
		for(const c of lastCallers) {
			if(c.isOwner || !c.onStage) continue;
			if(!(hide && claims.callerIds.has(c.callerId))) entries.push({ id: c.callerId, kind: 'guest', callerId: c.callerId, name: c.name || 'Guest', muted: !!c.micMuted });
			addScreen(c.callerId, c.screenId, c.name || 'Guest');
		}
		return entries;
	}

	function sync() {
		const entries = listEntries(true);
		if(!entries.length && listEntries(false).length) {
			// There's someone to show, they're just all in layers of their own - draw nothing rather than "waiting".
			placeholder.style.display = 'none';
			grid.style.display = 'none';
		} else if(!entries.length) {
			showEmpty(host.current?.excludeHost ? 'Waiting for guests to join…' : 'Waiting for someone to join…');
		} else {
			placeholder.style.display = 'none';
			grid.style.display = gridDisplay();
		}
		const wanted = new Set(entries.map(e => e.id));
		for(const id of [ ...tiles.keys() ]) if(!wanted.has(id)) removeTile(id);
		entries.forEach((entry, i) => {
			if(!tiles.has(entry.id)) addTile(entry);
			const tile = tiles.get(entry.id);
			tile.overlay.setName(entry.name);
			tile.overlay.setMuted(!!entry.muted);
			// Same order on every page: tiles are created as each page's streams happen to arrive, so DOM order alone
			// differed between the studio and the broadcast.
			tile.el.style.order = String(i);
		});
		// After the tiles exist, so a new one is sized too.
		if(entries.length) layoutGrid(entries.length);
	}

	let unsubscribeCallers = null;
	let unsubscribeOwner = null;
	let unsubscribeClaims = null;
	let unsubscribeOwnScreen = null;
	if(!host.callContext?.onCallers) {
		showEmpty('Dynamic grid needs an active call session');
	} else {
		unsubscribeClaims = host.callContext.onSceneClaims?.(c => { claims = c; sync(); });
		unsubscribeOwnScreen = host.callContext.onOwnScreen?.(id => { ownScreenId = id; sync(); });
		unsubscribeCallers = host.callContext.onCallers(callers => { lastCallers = callers || []; sync(); });
		unsubscribeOwner = host.callContext.onOwnerStream?.(stream => {
			// Any stream at all counts - an owner with only a mic gets a placeholder tile like any other guest.
			lastOwnerStream = stream || null;
			hasOwnerStream = !!stream;
			const ownerTile = tiles.get(OWNER_ID);
			if(ownerTile) setTileStream(ownerTile, lastOwnerStream);
			sync();
		});
	}

	// Re-pick the columns whenever the layer is resized.
	const resizeObserver = new ResizeObserver(() => layoutGrid());
	resizeObserver.observe(grid);

	host.onSettingsChange(() => sync());
	host.onDestroy(() => {
		resizeObserver.disconnect();
		unsubscribeCallers?.();
		unsubscribeOwner?.();
		unsubscribeClaims?.();
		unsubscribeOwnScreen?.();
		for(const id of [ ...tiles.keys() ]) removeTile(id);
		grid.remove();
		placeholder.remove();
	});
}
